import { Button } from '@heidi/design-system/button';
import { TypographyP, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { TrackWorkRpaDesktopRequiredShown } from '../../../contracts/desktop-required.types';
import { NudgeScene } from './WorkDiscoverSection.nudge-scene';

type ComputerUseNudgeCardBaseProps = {
  className?: string;
  downloadHref: string;
  onDismiss: () => void;
  onTrack: TrackWorkRpaDesktopRequiredShown;
};

export type ComputerUseNudgeCardProps = ComputerUseNudgeCardBaseProps &
  (
    | { variant?: 'teach'; onRemindLater: () => void; onRetry?: never }
    | { variant: 'retry'; onRetry: () => void; onRemindLater?: never }
  );

/**
 * Composer-dock CTA extracted from the agent-demo Nudge card
 * (``ChatStepsView.nudge.tsx`` in oscerai/scribe-fe-v2#17334).
 * Reuses the production {@link NudgeScene} illustration.
 *
 * ``teach`` is the web download card. ``retry`` is the desktop continue card
 * after the same on-demand miss — a new turn, not a resume of the failed
 * tool call.
 */
export function ComputerUseNudgeCard(props: ComputerUseNudgeCardProps): JSX.Element {
  const { className, downloadHref, onDismiss, onTrack } = props;
  const intl = useIntl();
  const trackedRef = useRef(false);
  const isRetry = props.variant === 'retry';

  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    onTrack({
      surface: 'project_chat',
      blockedAction: 'tool_result',
    });
  }, [onTrack]);

  const handleCta = (): void => {
    onTrack({
      surface: 'project_chat',
      blockedAction: 'tool_result',
      action: 'cta',
    });
    if (props.variant === 'retry') props.onRetry();
  };

  const handleRemindLater = (): void => {
    if (props.variant === 'retry') return;
    onTrack({
      surface: 'project_chat',
      blockedAction: 'tool_result',
      action: 'dismiss',
    });
    props.onRemindLater();
  };

  const handleDismiss = (): void => {
    onTrack({
      surface: 'project_chat',
      blockedAction: 'tool_result',
      action: 'dismiss',
    });
    onDismiss();
  };

  return (
    <div
      className={cn(
        'relative z-10 -mb-3 flex w-full items-stretch gap-4 rounded-b-none rounded-t-xl border border-b-0 border-border bg-surface p-3 pb-6',
        className
      )}
      data-testid="computer-use-nudge-card"
    >
      <NudgeScene />
      <div className="flex min-w-0 flex-1 flex-col gap-3 py-1 pe-10">
        <div className="flex min-w-0 flex-col">
          <TypographyP className="text-text-primary">
            {isRetry ? (
              <FormattedMessage
                defaultMessage="Continue on this computer"
                id="4mXSf6gKUY"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Teach Heidi to use your computer"
                id="CaUh/sivCf"
              />
            )}
          </TypographyP>
          <TypographyP2 className="text-text-secondary">
            {isRetry ? (
              <FormattedMessage
                defaultMessage="Heidi can retry the desktop task from here."
                id="8KhRfKCP9z"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Anything you need to do, Heidi can do for you"
                id="ZD/E+snWXt"
              />
            )}
          </TypographyP2>
        </div>
        <div className="flex items-center gap-2">
          {isRetry ? (
            <Button
              variant="default"
              size="sm"
              type="button"
              data-testid="computer-use-nudge-retry"
              onClick={handleCta}
            >
              <FormattedMessage defaultMessage="Retry" id="62nsdyMyIO" />
            </Button>
          ) : (
            <>
              <Button variant="default" size="sm" asChild>
                <a
                  data-testid="computer-use-nudge-setup"
                  href={downloadHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleCta}
                >
                  <FormattedMessage defaultMessage="Set up" id="rrGMSxQd9S" />
                </a>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                data-testid="computer-use-nudge-remind"
                onClick={handleRemindLater}
              >
                <FormattedMessage defaultMessage="Remind me later" id="c4hRLOnYpQ" />
              </Button>
            </>
          )}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        type="button"
        data-testid="computer-use-nudge-dismiss"
        aria-label={intl.formatMessage({
          defaultMessage: 'Dismiss',
          id: 'TDaF6JVgG6',
        })}
        onClick={handleDismiss}
        className="absolute end-3 top-3"
      >
        <X />
      </Button>
    </div>
  );
}
